import React, { memo } from 'react';
import MessageHeader from './MessageHeader';
import MessageTime from './MessageTime';
import FilesSection from './FilesSection';
import CopyButton from './CopyButton';
import ToolMessage from './ToolMessage';
import ToolCallIndicator from './ToolCallIndicator';
import { ChevronDown, ChevronRight, Brain } from 'lucide-react';
import { MarkdownRenderer, PlainTextRenderer } from '../../Renderers';
import { getMessageStyle } from '../utils/messageHelpers';
import { MESSAGE_TYPE_THINKING, MESSAGE_TYPE_TOOL, SENDER_USER } from '../constants';

/**
 * Individual message component
 * Renders different message types with appropriate styling and content
 */
const Message = memo(({ 
  message, 
  index, 
  messageId, 
  copiedMessageId, 
  copyToClipboard, 
  isStreaming, 
  expandedTools, 
  toggleToolExpansion, 
  searchQuery, 
  isHighlighted 
}) => {
  const getChatBubbleStyle = (sender) => {
    return 'rounded-2xl';
  };

  const bubbleClassName = `chat-message ${
    message.sender === SENDER_USER ? 'flex justify-end' : 'flex justify-start'
  }`;
  
  // Special handling for tool call messages with indicators
  if (message.isToolCall) {
    const contentClassName = `max-w-2xl border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-neutral-800`;
    
    // Auto-expand if streaming
    const shouldExpand = expandedTools[index] || 
                        (message.toolStatus?.isStreaming) || 
                        (message.toolStatus?.status === 'streaming');
    
    // Debug logging for streaming issues
    if (message.toolStatus?.streamingContent || message.toolStatus?.isStreaming) {
      console.log('Tool Call Message State:', {
        index,
        toolCallId: message.tool_call_id || message.toolCallId,
        toolName: message.tool_description || message.tool_name,
        status: message.toolStatus?.status,
        isStreaming: message.toolStatus?.isStreaming,
        hasStreamingContent: !!message.toolStatus?.streamingContent,
        streamingContentLength: message.toolStatus?.streamingContent?.length,
        expandedTools: expandedTools[index],
        shouldExpand,
        messageType: message.message_type,
        isToolCall: message.isToolCall
      });
    }
    
    return (
      <div id={`message-${index}`} className={bubbleClassName}>
        <div className={contentClassName}>
          <ToolCallIndicator
            toolName={message.tool_description || message.tool_name || 'Tool'}
            status={message.toolStatus?.status}
            error={message.toolStatus?.error}
            isExpanded={shouldExpand}
            onToggleExpand={() => toggleToolExpansion(index)}
            hasResult={!!message.toolStatus?.resultContent}
            onCopy={() => copyToClipboard(message.toolStatus?.resultContent || message.content, messageId)}
            copiedMessageId={copiedMessageId}
            messageId={messageId}
            resultContent={message.toolStatus?.resultContent}
            streamingContent={message.toolStatus?.streamingContent}
            isStreaming={message.toolStatus?.isStreaming}
            searchQuery={searchQuery}
            index={index}
          />
        </div>
      </div>
    );
  }

  if (message.message_type === MESSAGE_TYPE_THINKING) {
    const isExpanded = expandedTools[index] || isStreaming;

    return (
      <div id={`message-${index}`} className={bubbleClassName}>
        <div className="max-w-2xl border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden shadow-sm bg-neutral-50 dark:bg-neutral-900">
          <div className="flex items-center gap-2 px-4 py-3 text-xs font-medium text-neutral-600 dark:text-neutral-400">
            <Brain size={14} className={isStreaming ? 'text-brand-primary' : 'text-neutral-500 dark:text-neutral-400'} />
            <span>Thinking</span>
            {isStreaming && (
              <span className="inline-flex items-center ml-1">
                <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse"></span>
                <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse animation-delay-200"></span>
                <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse animation-delay-400"></span>
              </span>
            )}
            <button
              onClick={() => toggleToolExpansion(index)}
              className="ml-auto text-neutral-600 dark:text-neutral-400"
              title={isExpanded ? 'Hide thinking' : 'Show thinking'}
              aria-label={isExpanded ? 'Hide thinking' : 'Show thinking'}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          </div>
          {isExpanded && (
            <div className="px-4 pb-4 text-sm text-neutral-700 dark:text-neutral-300">
              <div className="max-h-64 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 p-3">
                <MarkdownRenderer
                  content={message.content}
                  searchQuery={searchQuery}
                  messageIndex={index}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  const contentClassName = `max-w-2xl ${
    message.message_type === MESSAGE_TYPE_TOOL 
      ? 'border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-neutral-800' 
      : getChatBubbleStyle(message.sender) + ' p-4 ' + getMessageStyle(message.sender, message.isSubAgent, message.agentType)
  } relative group transition-all ${isHighlighted ? 'search-highlight-message' : ''}`;

  return (
    <div id={`message-${index}`} className={bubbleClassName}>
      <div className={contentClassName}>
        {message.sender !== SENDER_USER && !isStreaming && message.message_type !== MESSAGE_TYPE_TOOL && (
          <CopyButton 
            messageId={messageId} 
            copiedMessageId={copiedMessageId} 
            onCopy={() => copyToClipboard(message.content, messageId)} 
          />
        )}
        
        {message.message_type === MESSAGE_TYPE_TOOL && !message.isToolCall ? (
          // Only show old ToolMessage for tool messages that aren't processed tool calls
          <ToolMessage 
            message={message}
            messageId={messageId}
            copiedMessageId={copiedMessageId}
            isExpanded={expandedTools[index]}
            toggleExpansion={toggleToolExpansion}
            isStreaming={isStreaming}
            onCopy={() => copyToClipboard(message.content, messageId)}
            index={index}
            searchQuery={searchQuery}
          />
        ) : (
          <>
            <MessageHeader 
              sender={message.sender} 
              isSubAgent={message.isSubAgent} 
              agentType={message.agentType} 
            />
            
            {message.sender === SENDER_USER ? (
              <PlainTextRenderer 
                content={message.content} 
                searchQuery={searchQuery} 
                messageIndex={index} 
              />
            ) : (
              <MarkdownRenderer 
                content={message.content} 
                searchQuery={searchQuery} 
                messageIndex={index} 
              />
            )}
            
            {isStreaming && (
              <span className="inline-flex items-center ml-1 mt-2">
                <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse"></span>
                <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse animation-delay-200"></span>
                <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse animation-delay-400"></span>
              </span>
            )}
            
            <FilesSection files={message.files} />
            
            <MessageTime 
              timestamp={message.timestamp} 
              sender={message.sender} 
              isStreaming={isStreaming} 
              agentType={message.agentType}
            />
          </>
        )}
      </div>
    </div>
  );
});

Message.displayName = 'Message';

export default Message;
